<template>
  <v-dialog
    v-model="internalValue"
    max-width="420"
    persistent
    content-class="rounded-dialog"
  >
    <v-card class="rounded-xl overflow-hidden">
      <!-- Dynamic Header with Gradient -->
      <v-card-title class="title_for_modal" :class="headerClass">
        <v-icon v-if="!loading" :color="headerIconColor" class="mr-2">
          {{ headerIcon }}
        </v-icon>
        <span>{{ headerText }}</span>
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Loading State: Enhanced with Animation -->
        <div v-if="loading" class="dialog-center">
          <div class="image-container mb-4">
            <div class="pulse-animation">
              <v-img
                :src="require('@/assets/img/phone.png')"
                alt="Submitting Order"
                class="order-image"
              />
            </div>
          </div>

          <!-- Custom Loading Animation -->
          <div class="loading-container mb-4">
            <v-progress-circular
              indeterminate
              color="#f85757"
              size="50"
              width="4"
              class="loading-spinner"
            ></v-progress-circular>
            <div class="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>

          <div class="status-text loading-text">
            Please wait while we process your order
          </div>
          <div class="sub-text">This may take a few moments...</div>
        </div>

        <!-- Error State: Enhanced with Better Visual Feedback -->
        <div v-else-if="error" class="dialog-center">
          <div class="error-icon-container mb-4">
            <v-icon color="#ff5252" size="80" class="error-icon">
              mdi-alert-circle-outline
            </v-icon>
          </div>
          <div class="status-text error-text mb-2">
            Oops! Something went wrong
          </div>
          <div class="error-message">
            {{
              errorMessage || "Unable to process your order. Please try again."
            }}
          </div>
        </div>

        <!-- Success State: Enhanced with Celebration Feel -->
        <div v-else class="dialog-center">
          <div class="image-container mb-4">
            <div class="success-animation">
              <v-img
                :src="require('@/assets/img/slurp-logo-basic-01.png')"
                alt="Order Success"
                class="order-image"
              />
            </div>
          </div>

          <!-- Success Checkmark Animation -->
          <div class="success-check mb-4">
            <v-icon color="#4caf50" size="60" class="check-icon">
              mdi-check-circle
            </v-icon>
          </div>

          <div class="status-text success-text mb-2">
            Order Received Successfully!
          </div>
          <div class="success-message">
            {{
              successMessage ||
              "Thank you for your order. We'll process it shortly."
            }}
          </div>
        </div>
      </v-card-text>

      <!-- Enhanced Action Button -->
      <v-card-actions v-if="!loading" class="pa-0">
        <v-btn
          :color="buttonColor"
          class="ok-btn elevation-0"
          @click="handleOk"
          block
          large
          :ripple="{ class: 'white--text' }"
        >
          <v-icon left class="mr-2">{{ buttonIcon }}</v-icon>
          <span class="ok-text">{{ buttonText }}</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "OrderSendingDialog",
  props: {
    value: Boolean,
    loading: Boolean,
    successMessage: String,
    error: Boolean,
    errorMessage: String,
  },
  data() {
    return {
      internalValue: this.value,
    };
  },
  computed: {
    headerClass() {
      if (this.loading) return "loading-header";
      if (this.error) return "error-header";
      return "success-header";
    },
    headerText() {
      if (this.loading) return "Processing Your Order";
      if (this.error) return "Order Failed";
      return "Order Confirmed";
    },
    headerIcon() {
      if (this.error) return "mdi-alert";
      return "mdi-check";
    },
    headerIconColor() {
      return "white";
    },
    buttonColor() {
      if (this.error) return "#ff5252";
      return "#4caf50";
    },
    buttonText() {
      if (this.error) return "Try Again";
      return "Continue";
    },
    buttonIcon() {
      if (this.error) return "mdi-refresh";
      return "mdi-arrow-right";
    },
  },
  watch: {
    value(val) {
      this.internalValue = val;
    },
    internalValue(val) {
      this.$emit("input", val);
    },
  },
  methods: {
    handleOk() {
      this.internalValue = false;
      this.$emit("ok");
    },
  },
};
</script>

<style lang="scss" scoped>
.title_for_modal {
  color: #fff !important;
  text-align: center;
  width: 100%;
  font-weight: 600;
  padding: 24px 16px !important;
  font-size: 18px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  box-sizing: border-box;
  margin: 0 !important;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &.loading-header {
    background: linear-gradient(135deg, #f85757, #ff7b7b) !important;
  }

  &.error-header {
    background: linear-gradient(135deg, #ff5252, #ff7979) !important;
  }

  &.success-header {
    background: linear-gradient(135deg, #4caf50, #66bb6a) !important;
  }
}

.dialog-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  text-align: center;
}

.image-container {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.order-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.pulse-animation {
  animation: pulse 2s infinite;
}

.success-animation {
  animation: bounceIn 0.6s ease-out;
}

.loading-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  animation: spin 1s linear infinite;
}

.loading-dots {
  position: absolute;
  display: flex;
  gap: 4px;

  span {
    width: 6px;
    height: 6px;
    background-color: #f85757;
    border-radius: 50%;
    animation: loadingDots 1.4s infinite ease-in-out both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }
    &:nth-child(2) {
      animation-delay: -0.16s;
    }
    &:nth-child(3) {
      animation-delay: 0s;
    }
  }
}

.status-text {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;

  &.loading-text {
    color: #333;
  }

  &.error-text {
    color: #ff5252;
  }

  &.success-text {
    color: #4caf50;
  }
}

.sub-text {
  font-size: 14px;
  color: #666;
  margin-top: 8px;
}

.error-message,
.success-message {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  max-width: 280px;
}

.error-icon-container {
  animation: shake 0.5s ease-in-out;
}

.success-check {
  animation: bounceIn 0.8s ease-out 0.3s both;
}

.check-icon {
  filter: drop-shadow(0 2px 4px rgba(76, 175, 80, 0.3));
}

.ok-btn {
  width: 100% !important;
  border-radius: 0 0 12px 12px !important;
  min-height: 56px;
  justify-content: center;
  align-items: center;
  display: flex;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}

.ok-text {
  color: #fff !important;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0.5px;
}

// Animations
@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

@keyframes bounceIn {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-5px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(5px);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes loadingDots {
  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>

<style lang="scss">
.rounded-dialog {
  border-radius: 16px !important;
  overflow: hidden !important;
  background-color: transparent !important;
  box-shadow: none !important;
}
</style>
